import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Calendar, TrendingUp, Gem } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  publish_date: string;
  post_type: string;
  tokens_mentioned: string[] | null;
}

const ArticleArchive = () => {
  const [selectedType, setSelectedType] = useState<string>('all');

  const { data: posts, isLoading } = useQuery({
    queryKey: ['blog-posts-archive'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, publish_date, post_type, tokens_mentioned')
        .eq('status', 'published')
        .order('publish_date', { ascending: false });
      
      if (error) throw error;
      return data as BlogPost[];
    },
  });

  const filteredPosts = posts?.filter(post => 
    selectedType === 'all' || post.post_type === selectedType
  );

  const getPostTypeIcon = (type: string) => {
    switch (type) {
      case 'best_coin':
        return <TrendingUp className="w-4 h-4" />;
      case 'best_roi':
        return <Gem className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const getPostTypeBadge = (type: string) => {
    switch (type) {
      case 'best_coin':
        return <Badge className="bg-primary">Best Coin Today</Badge>;
      case 'best_roi':
        return <Badge className="bg-gold">Best ROI Token</Badge>;
      case 'daily_digest':
        return <Badge variant="secondary">Daily Digest</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-2">Article Archive</h1>
          <p className="text-muted-foreground">
            Browse all published cryptocurrency analysis and recommendations
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={selectedType === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedType('all')}
            size="sm"
          >
            All Articles
          </Button>
          <Button
            variant={selectedType === 'best_coin' ? 'default' : 'outline'}
            onClick={() => setSelectedType('best_coin')}
            size="sm"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Best Coin
          </Button>
          <Button
            variant={selectedType === 'best_roi' ? 'default' : 'outline'}
            onClick={() => setSelectedType('best_roi')}
            size="sm"
          >
            <Gem className="w-4 h-4 mr-2" />
            Best ROI
          </Button>
          <Button
            variant={selectedType === 'daily_digest' ? 'default' : 'outline'}
            onClick={() => setSelectedType('daily_digest')}
            size="sm"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Daily Digest
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Articles</CardDescription>
              <CardTitle className="text-3xl">{posts?.length || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Best Coin Picks</CardDescription>
              <CardTitle className="text-3xl">
                {posts?.filter(p => p.post_type === 'best_coin').length || 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>ROI Gems</CardDescription>
              <CardTitle className="text-3xl">
                {posts?.filter(p => p.post_type === 'best_roi').length || 0}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Articles Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredPosts && filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredPosts.map((post) => (
              <Link to={`/article/${post.slug}`} key={post.id}>
                <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        {getPostTypeIcon(post.post_type)}
                        {getPostTypeBadge(post.post_type)}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(post.publish_date), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    <CardTitle className="line-clamp-2 hover:text-primary transition-colors">
                      {post.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                      {post.excerpt}
                    </p>
                    
                    {post.tokens_mentioned && post.tokens_mentioned.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {post.tokens_mentioned.slice(0, 3).map((token) => (
                          <Badge key={token} variant="outline" className="text-xs">
                            {token}
                          </Badge>
                        ))}
                        {post.tokens_mentioned.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{post.tokens_mentioned.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                No articles found. Run the automation to generate content.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ArticleArchive;
